import { GridColDef, WithLoading } from "@biggeo/bg-ui";
import {
    Box,
    Button,
    ColFilterType,
    DataGridContainer,
    EmptyScreen,
    IconAvatar,
    IFilterSearchPaginate,
    MemoizedField,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from "@biggeo/bg-ui/lab";
import { AddOutline, BigGeoLogo, Info } from "@biggeo/bg-ui/lab/icons";
import isEqual from "lodash/isEqual";
import startCase from "lodash/startCase";
import { useState } from "react";
import { Routes } from "../../../navigation/redux/model";
import { DEFAULT_LOGIC_OPERATOR } from "../../filter-criteria/utils/utils";
import {
    getDefaultDatasetConfiguration,
    setDatasetContext,
} from "../context/utils/general-utils";
import {
    ComputeStatus,
    InputUpdateMapTemplateDataset,
    MapTemplateDatasetExtended,
    useUpdateMapTemplateDatasetMutation,
} from "@biggeo/bg-server-lib/studio";
import SwitchToDatalabModal from "../../../common/components/SwitchToDatalabModal";
import { compose } from "redux";
import { modalActions } from "../../../modal/redux/model";
import { useDispatch } from "react-redux";
import bytes from "bytes";
import { Link } from "react-router-dom";
import { CenteredLayout } from "@biggeo/bg-ui/lab/layouts";

const tooltips: {
    computeUsage: string;
    mapUse: string;
    enable: string;
    compute: string;
    defaultColor: string;
} = {
    computeUsage: "The percentage this dataset uses in your available compute",
    mapUse: "Make dataset accessible in the maps interface datasets panel",
    enable: "This determines whether or not this map template has access to the dataset",
    compute:
        "This determines whether or not your data is actively using your available compute",
    defaultColor:
        "The default color for this dataset starts with on blank templates",
};

interface IMapDatasetsConfigGrid {
    readonly mapTemplateDatasets: readonly MapTemplateDatasetExtended[];
    readonly refetchMapTemplateDatasets: () => void;
    readonly totalRows: number;
    readonly filterSearchPaginateProps: IFilterSearchPaginate;
    readonly loading: boolean;
    readonly manageMapTemplate: () => void;
    readonly noDatasetInfo: string;
}

const MapDatasetsConfigGrid = ({
    mapTemplateDatasets,
    refetchMapTemplateDatasets,
    totalRows,
    filterSearchPaginateProps,
    loading,
    manageMapTemplate,
    noDatasetInfo,
}: IMapDatasetsConfigGrid) => {
    const dispatch = useDispatch();
    const { addDatasets, removeDataset } = setDatasetContext();

    const [currentRowId, setCurrentRowId] = useState<string | undefined>(
        undefined
    );
    const openModal = compose(dispatch, modalActions.openModal);
    const closeModal = compose(dispatch, modalActions.closeModal);

    const { executeMutation: updateMapTemplateDataset } =
        useUpdateMapTemplateDatasetMutation();

    const update = (input: InputUpdateMapTemplateDataset) => {
        setCurrentRowId(input.id);

        updateMapTemplateDataset({
            variables: {
                input,
            },
            onCompleted: (data) => {
                setCurrentRowId(undefined);
                refetchMapTemplateDatasets();

                const mapUse =
                    data.updateMapTemplateDataset.mapTemplateDataset.mapUse;

                if (mapUse) {
                    addDatasets({
                        dataSource: data.updateMapTemplateDataset.dataSource,
                        dataSourceConfiguration:
                            data.updateMapTemplateDataset
                                .dataSourceConfiguration,
                        mapTemplateDataset:
                            data.updateMapTemplateDataset.mapTemplateDataset,
                        isSelected: false,
                        isVisible: false,
                        isGettingStyled: false,
                        isLegendOpen: false,
                        isTableViewed: false,
                        configuration: getDefaultDatasetConfiguration(),
                        filters: {
                            logicOperator: DEFAULT_LOGIC_OPERATOR,
                            filters: [],
                        },
                        selectedRows: [],
                        pinnedRows: [],
                        isSample: false,
                        fkMarketplaceDatasetId: undefined,
                        organization: undefined,
                        image: undefined,
                    });
                } else {
                    removeDataset({
                        dataSourceId:
                            data.updateMapTemplateDataset.dataSource.id,
                        mapTemplateDatasetId:
                            data.updateMapTemplateDataset.mapTemplateDataset.id,
                    });
                }
            },
        });
    };

    const onClickDatalabDataManagement = () => {
        openModal({
            modalType: "new-dialog",
            component: (
                <SwitchToDatalabModal
                    onClose={closeModal}
                    link={Routes.datalabDataManagement}
                />
            ),
            dialogProps: {
                variant: "centered-mid-small",
            },
        });
    };

    const columns: GridColDef<MapTemplateDatasetExtended>[] = [
        {
            field: "label",
            headerName: "Name",
            flex: 1,
            minWidth: 378,
            sortable: false,
            resizable: false,
            type: ColFilterType.string,
            filterable: true,
            renderCell: (params) => (
                <MemoizedField
                    disable={
                        params.row.dataSourceConfiguration.status !==
                        ComputeStatus.hot
                    }
                    title={
                        params.row.dataSource.name ||
                        params.row.dataSource.tableName
                    }
                    subTitle={params.row.dataSource.description || undefined}
                />
            ),
        },
        {
            field: "defaultColor",
            headerName: "Default Color",
            headerAlign: "center",
            flex: 1,
            minWidth: 176,
            sortable: false,
            resizable: false,
            type: ColFilterType.string,
            renderHeader: (params) => (
                <Stack flexDirection="row" alignItems="center" gap={1}>
                    <Typography variant="body3" fontWeight="semibold">
                        {startCase(params.colDef.headerName)}
                    </Typography>
                    <Tooltip title={tooltips.defaultColor} sx={{ width: 45 }}>
                        <span>
                            <Info color="primary" size="xs" />
                        </span>
                    </Tooltip>
                </Stack>
            ),
            renderCell: (params) => {
                return (
                    <MemoizedField
                        disable={
                            params.row.dataSourceConfiguration.status !==
                            ComputeStatus.hot
                        }
                    >
                        <Box
                            sx={{
                                backgroundColor:
                                    params.row.mapTemplateDataset?.styles.fill
                                        .color,
                                width: 6,
                                height: 6,
                            }}
                        />
                    </MemoizedField>
                );
            },
        },
        {
            field: "src",
            headerName: "Src",
            minWidth: 150,
            sortable: false,
            headerAlign: "center",
            resizable: false,
            type: ColFilterType.string,
            renderCell: (params) => (
                <MemoizedField
                    disable={
                        params.row.dataSourceConfiguration.status !==
                        ComputeStatus.hot
                    }
                >
                    <IconAvatar color="primary" size="xs">
                        <BigGeoLogo />
                    </IconAvatar>
                </MemoizedField>
            ),
        },
        {
            field: "size",
            headerName: "Size",
            minWidth: 150,
            sortable: false,
            headerAlign: "left",
            type: ColFilterType.number,
            filterable: true,
            renderCell: (params) => {
                const size = bytes(Number(params.row.dataSource.size) || 0);

                return (
                    <MemoizedField
                        disable={
                            params.row.dataSourceConfiguration.status !==
                            ComputeStatus.hot
                        }
                        title={size}
                        sx={{ justifyContent: "flex-start", textAlign: "left" }}
                    />
                );
            },
        },
        {
            field: "manage",
            sortable: false,
            headerName: "",
            minWidth: 110,
            renderCell: () => {
                return (
                    <Stack sx={{ width: "100%" }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            density="dense"
                            onClick={() => onClickDatalabDataManagement()}
                        >
                            Manage
                        </Button>
                    </Stack>
                );
            },
        },
        {
            field: "mapUse",
            headerName: "Map use",
            minWidth: 115,
            sortable: false,
            type: ColFilterType.boolean,
            renderHeader: (params) => (
                <Stack flexDirection="row" alignItems="center" gap={1}>
                    <Typography variant="body3" fontWeight="semibold">
                        {startCase(params.colDef.headerName)}
                    </Typography>
                    <Tooltip title={tooltips.mapUse} sx={{ width: 45 }}>
                        <span>
                            <Info color="primary" size="xs" />
                        </span>
                    </Tooltip>
                </Stack>
            ),
            renderCell: (params) => (
                <MemoizedField
                    disable={
                        params.row.dataSourceConfiguration.status !==
                        ComputeStatus.hot
                    }
                >
                    <WithLoading
                        loading={
                            isEqual(
                                currentRowId,
                                params.row.mapTemplateDataset?.id
                            )
                                ? loading
                                : false
                        }
                    >
                        <Switch
                            size="small"
                            color="surface"
                            switched={params.row.mapTemplateDataset?.mapUse}
                            onSwitchChange={() => {
                                setCurrentRowId(
                                    params.row.mapTemplateDataset?.id
                                );

                                update({
                                    id: params.row.mapTemplateDataset.id,
                                    mapUse:
                                        !params.row.mapTemplateDataset.mapUse,
                                });
                            }}
                        />
                    </WithLoading>
                </MemoizedField>
            ),
        },
    ];

    return (
        <DataGridContainer
            getRowId={(row) => row.mapTemplateDataset.id}
            pinnedColumns={{ right: ["mapUse", "manage"] }}
            columns={columns}
            rows={mapTemplateDatasets}
            rowCount={totalRows}
            loading={loading}
            title={"Datasets"}
            filterSearchPaginateProps={filterSearchPaginateProps}
            disableColumnResize
            customNoRowsOverlay={
                <CenteredLayout
                    maxWidth="800px"
                    slotProps={{
                        main: {
                            height: "100%",
                        },
                    }}
                >
                    <EmptyScreen
                        title={"No Datasets Enabled"}
                        subtitle={noDatasetInfo}
                        image={
                            "https://sabiggeomedia01.blob.core.windows.net/test/f0896fd5-e036-2957-9c1c-61dfa1ff8cf9.png.png"
                        }
                        buttons={[
                            {
                                startNode: <AddOutline />,
                                onClick: () => {
                                    manageMapTemplate();
                                },
                                children: "Enable Datasets",
                                variant: "filled",
                                color: "primary",
                            },
                            {
                                children: (
                                    <Link
                                        to={
                                            "https://datascape.featurebase.app/help/articles/6375162-use-map-datasets"
                                        }
                                    >
                                        <Typography
                                            variant="body3"
                                            fontWeight="semibold"
                                        >
                                            Learn More
                                        </Typography>
                                    </Link>
                                ),
                            },
                        ]}
                    />
                </CenteredLayout>
            }
        />
    );
};

export default MapDatasetsConfigGrid;
